<template>
  <modal width="560px" :title="t('Update Payment Method')" @close="close">
    <div style="width: 100%; display: flex; flex-direction: column; align-items: center">
      <div v-if="stripeLoading" class="update-payment-method__loading">
        <loading-circle width="32" height="32" layout="inline" />
      </div>
      <div
        v-show="!stripeLoading"
        style="width: 100%; display: flex; flex-direction: column; align-items: center"
      >
        <div class="update-payment-method__body card-text-inner">
          <div class="p-0">
            <label class="payment_label">{{ t('Card Number') }}</label>
            <div ref="cardNumber" class="payment_input mb-8" />
          </div>
          <div class="row-2-elements">
            <div class="p-0">
              <label class="payment_label">{{ t('Expiration Date') }}</label>
              <div ref="cardExpiry" class="payment_input" />
            </div>
            <div class="p-0">
              <label class="text-uppercase payment_label">{{ t('CVV / CVC') }}</label>
              <div ref="cardCvc" class="payment_input" />
            </div>
          </div>
        </div>
        <div class="cards">
          <img src="/images/payment/visa.svg" alt="visa" />
          <img src="/images/payment/mastercard.svg" alt="mastercard" />
          <img src="/images/payment/unionpay.svg" alt="unionpay" />
          <img src="/images/payment/american_express.svg" alt="american express" />
          <img src="/images/payment/discover.svg" alt="discover" />
          <img src="/images/payment/diners_club.svg" alt="diners club" />
          <img src="/images/payment/jcb.svg" alt="jcb" />
        </div>
      </div>
    </div>
    <template #footer>
      <section class="d-flex justify-content-between align-items-center w100 xs12">
        <error-aggregator
          class="aggregator-wrap"
          v-model="errors"
          v-model:active="errorsControlActive"
        />
        <a href="https://stripe.com/" target="_blank" class="powered-by-stripe">
          <img src="/images/payment/powered-by-stripe.svg" alt="powered by stripe" />
        </a>
        <div class="justify-end max-content" style="display: flex">
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            :disabled="stripeLoading"
            @click="close"
          />

          <ui-button
            v-if="backCallback"
            :text="t('Back')"
            icon="fas fa-arrow-left"
            :disabled="loading || stripeLoading"
            @click="backCallback"
          />

          <ui-button
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            :loading="loading"
            :disabled="stripeLoading"
            @click="submit"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import config from '@/config';
import LoadingCircle from '@/components/loading-circle.vue';
import {
  getBillingCustomerApiRequest,
  postBillingSetupIntentApiRequest,
  postUpdateBillingCardApiRequest,
} from '@/services/api/billing-api';
import { useStore } from '@/store';
import { useColors, useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';

export interface UpdatePaymentMethodDialogOpenOptions {
  backCallback?: () => void;
}

const props = withDefaults(
  defineProps<{
    data?: UpdatePaymentMethodDialogOpenOptions;
  }>(),
  {
    data: () => ({}),
  },
);

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'success', time: string): void;
}>();

const { t, locale } = useI18n();
const $colors = useColors();
const store = useStore();

const loading = ref(false);
const stripeLoading = ref(true);
const errors = ref<string[]>([]);
const errorsControlActive = ref(false);

const stripe = ref<any>(null);
const cardElements = ref<any>(null);
const cardNumberElement = ref<any>(null);
const cardExpiryElement = ref<any>(null);
const cardCvcElement = ref<any>(null);

const cardNumber = ref<HTMLElement | null>(null);
const cardExpiry = ref<HTMLElement | null>(null);
const cardCvc = ref<HTMLElement | null>(null);

const close = () => {
  store.setLoading(false);
  emit('close');
};

const onStripeReady = () => {
  stripeLoading.value = true;
  if ((window as any).Stripe === undefined) {
    $emitter.emit('toast-error', t("Stripe library isn't loaded"));
    stripeLoading.value = false;
    return;
  }

  stripe.value = (window as any).Stripe(config.stripeToken, {
    locale: locale.value,
  });

  const style = {
    base: {
      fontSize: '12px',
      color: $colors.graphite,
      lineHeight: '32px',
      fontFamily: 'Lato, Helvetica, sans-serif, -apple-system',
      letterSpacing: '0.6px',
    },
    invalid: {
      color: $colors.graphite,
      borderColor: $colors.red,
    },
  };

  cardElements.value = stripe.value.elements();
  cardNumberElement.value = cardElements.value.create('cardNumber', { style });
  cardNumberElement.value.mount(cardNumber.value);
  cardExpiryElement.value = cardElements.value.create('cardExpiry', { style });
  cardExpiryElement.value.mount(cardExpiry.value);
  cardCvcElement.value = cardElements.value.create('cardCvc', { style });
  cardCvcElement.value.mount(cardCvc.value);

  stripeLoading.value = false;
  store.setLoading(false);
};

onMounted(() => {
  if ((window as any).Stripe) {
    onStripeReady();
    return;
  }

  const stripeLibrary = document.createElement('script');
  stripeLibrary.src = 'https://js.stripe.com/v3/';
  document.head.appendChild(stripeLibrary);
  stripeLibrary.onload = onStripeReady;
});

const backCallback = computed(() => {
  if (!props.data?.backCallback) return;
  return () => {
    close();
    props.data.backCallback?.();
  };
});

const submit = async () => {
  if (stripeLoading.value) return;
  errors.value = [];
  errorsControlActive.value = false;
  loading.value = true;
  store.setLoading(true);

  const createdPaymentMethod = await stripe.value.createPaymentMethod(
    'card',
    cardNumberElement.value,
  );
  if (createdPaymentMethod.error) {
    submitErrorHandler(createdPaymentMethod.error.message);
    return;
  }

  const setupIntent = (await postBillingSetupIntentApiRequest({
    paymentMethodId: createdPaymentMethod.paymentMethod.id,
  })) as any;

  if (
    setupIntent.status === 'requires_action' ||
    setupIntent.status === 'requires_source_action'
  ) {
    const confirmedCardSetup = await stripe.value.confirmCardSetup(
      setupIntent.client_secret,
    );
    if (confirmedCardSetup.error) {
      submitErrorHandler(confirmedCardSetup.error.message);
      return;
    }
    await submitSuccess(confirmedCardSetup.setupIntent.id);
    return;
  }

  if (
    setupIntent.status === 'requires_payment_method' ||
    setupIntent.status === 'requires_source'
  ) {
    submitErrorHandler(t('Your card was denied, please provide a new payment method'));
    return;
  }

  if (setupIntent.status === 'succeeded') {
    await submitSuccess(setupIntent.id);
    return;
  }

  submitErrorHandler(t('Validation failed'));
};

const submitErrorHandler = (error: string) => {
  store.setLoading(false);
  loading.value = false;
  errors.value = [t('Validation failed'), error];
  errorsControlActive.value = true;
};

const submitSuccess = async (token: string) => {
  await postUpdateBillingCardApiRequest(
    {
      token,
      gateway: 'stripe3ds',
    },
    true,
  );
  const customer = (await getBillingCustomerApiRequest()) as any;
  store.setBillingCustomer(customer);
  $emitter.emit('toast-success', t('Payment method successfully changed.'));
  close();
};
</script>

<style lang="scss">
.update-payment-method__expiration-year {
  .v-label {
    opacity: 0;
  }
}

.update-payment-method__expiration-month {
  width: 60px;
  flex-shrink: 0;
}

.update-payment-method__expiration-month.v-text-field input {
  width: 60px !important;
}

.update-payment-method__expiration-date-divider {
  margin: 0 8px;
}

.update-payment-method__loading {
  min-height: 180px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.v-dialog.v-dialog--active {
  overflow: auto;

  .aggregator-wrap {
    flex: unset !important;
  }
}
</style>
<style lang="scss" scoped>
.row-2-elements {
  display: flex;
  column-gap: 16px;
  & > div {
    flex: 1 0 0;
  }
}
.cards {
  img {
    margin: 33px 6px 0;
    height: 20px;
  }
}
.card-text-inner {
  width: 270px;
}

.payment_input {
  padding-left: 8px;
  padding-right: 4px;
  border-radius: 6px;
  border: solid 1px var(--color-gray20);
  background-color: var(--color-white);
  margin-top: 4px;
}

.payment_label {
  font-weight: bold;
}

.powered-by-stripe {
  flex: 0 0 auto !important;
  align-self: center;
  img {
    display: block;
  }

  @media (max-width: 600px) {
    display: none;
  }
}
</style>
